import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowUpRight } from "lucide-react";

const CERTIFICATE_IMAGE = "/assets/premio-sabor-granada-2023.png";
const CERTIFICATE_WIDTH = 1459;
const CERTIFICATE_HEIGHT = 1078;

/**
 * Editorial award block used on the landing page.
 *
 * Copy lives in ecommerce.js → `award.*`:
 *   overline · title · body · cta · cta_href · alt
 *
 * The image is the actual certificate published by Cortijo de Santa María,
 * self-hosted at /public/assets/. Clicking the CTA opens the dedicated
 * verification page — no external links, no invented schema.
 */
export default function Award() {
  const { t } = useLang();
  const alt = t("award.alt");
  const overline = t("award.overline");
  const title = t("award.title");
  const body = t("award.body");
  const cta = t("award.cta");
  const ctaHref = t("award.cta_href");

  return (
    <section
      data-testid="award-section"
      aria-labelledby="award-heading"
      className="bg-olive-900 text-cream-100 border-t border-olive-800/60"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 sm:py-20">
        <div className="grid sm:grid-cols-[auto_1fr] items-center gap-10 sm:gap-16">
          <Link
            to={ctaHref}
            data-testid="award-image-link"
            className="justify-self-center sm:justify-self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-sm"
            aria-label={cta}
          >
            <img
              src={CERTIFICATE_IMAGE}
              alt={alt}
              width={CERTIFICATE_WIDTH}
              height={CERTIFICATE_HEIGHT}
              loading="lazy"
              decoding="async"
              data-testid="award-image"
              className="h-52 sm:h-64 lg:h-72 w-auto object-contain drop-shadow-[0_14px_36px_rgba(0,0,0,0.4)]"
            />
          </Link>

          <div className="max-w-xl text-center sm:text-left">
            <span
              data-testid="award-overline"
              className="text-[11px] uppercase tracking-[0.32em] text-gold font-medium"
            >
              {overline}
            </span>
            <h2
              id="award-heading"
              data-testid="award-title"
              className="mt-4 font-serif text-3xl sm:text-4xl lg:text-[40px] leading-[1.1] text-cream-100"
            >
              {title}
            </h2>
            <p
              data-testid="award-body"
              className="mt-5 font-light text-cream-100/85 text-base sm:text-lg leading-relaxed"
            >
              {body}
            </p>
            <Link
              to={ctaHref}
              data-testid="award-cta"
              className="mt-8 inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.26em] text-gold hover:text-cream-100 border-b border-gold/40 hover:border-cream-100 pb-1 transition-colors"
            >
              {cta}
              <ArrowUpRight size={16} strokeWidth={1.4} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
